import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Button, CircularProgress, Container, Stack, Typography } from '@mui/material';
import { invoke } from '@tauri-apps/api/core';
import { AddressFetchWithBody, AddressMutate } from 'cloak-auth/src/Client/AddressFetcher';
import { useGetInstances } from 'gen/client';
import { InstanceDTO } from 'gen/model';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';

import { useSessionsStore } from '@ui/Components/Backend/sessionsStore';

import { RtcComponent, SessionsObj } from 'kerberus-components/src/Components/Backend/RtcComponent';
import { TauriRtcComponent } from 'kerberus-components/src/Components/Backend/TauriRtcComponent';
import { UIDeviceGroup } from 'kerberus-components/src/Types/WizardSetup';

const delayMs = 1000;
const simulateDelay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const handleDiscoverBrowser = async (
    id?: string | null,
    deviceGroups?: UIDeviceGroup[],
    sessions?: sessionsType,
    rtc?: rtcType,
    update?: (macList: MacListProps[]) => Promise<void>,
) => {
    if (sessions) {
        if (!sessions.arp.length) {
            console.log('No available ARP sessions');
            return [];
        }
        const foundList: Car[] = [];

        for (const session of sessions.arp) {
            try {
                const mac = await rtc!.createRtc('browser', session);
                console.log('📇 ~ mac:', mac);
                if (mac) {
                    const existing = deviceGroups?.find((group) =>
                        group.addresses.some((i) => i.address === mac.toLowerCase()),
                    );
                    console.log('📇 ~ existing:', existing);
                    console.log('📇 ~ found:', mac);

                    foundList.push({ address: mac, bluetooth: false });
                }
            } catch (error) {
                console.log('Err from', session, error);
                return [];
            }
        }

        if (update && foundList.length) {
            await update(foundList);
            return foundList;
        }

        console.log('task 1');
        return foundList;
    } else {
        console.log('No sessions');
    }
};

const handleDiscoverHeadless = async (
    id?: string | null,
    deviceGroups?: UIDeviceGroup[],
    sessions?: sessionsType,
    rtc?: rtcType,
    update?: (macList: MacListProps[]) => Promise<void>,
    instance?: InstanceDTO,
) => {
    if (sessions) {
        if (!sessions.headless.length) {
            console.log('No available HEADLESS sessions');
            return [];
        }
        const foundList: Car[] = [];

        for (const session of sessions.headless.filter((i) => i.instance === instance?.id)) {
            try {
                const mac = await rtc!.createRtc('headless', session.id);
                if (mac) {
                    console.log('MAC:', mac);

                    const existing = deviceGroups?.find((group) =>
                        group.addresses.some((i) => i.address === mac.toLowerCase()),
                    );

                    console.log(existing);
                    console.log(mac);
                    foundList.push({ address: mac, bluetooth: false });
                }
            } catch (error) {
                console.log('Err from', session, error);
                return [];
            }
        }

        if (update && foundList.length) {
            await update(foundList);
        }

        console.log('task 2');
        return foundList;
    } else {
        console.log('No sessions');
    }
};

interface MacListProps {
    address: string;
    bluetooth: false;
}

interface TaskStatus {
    error: boolean;
    loading: boolean;
    success: boolean;
    taskNotFound: boolean;
}

interface Car {
    address: string;
    bluetooth: false;
    model?: string;
}

type sessionsType = SessionsObj | null;
type rtcType = RtcComponent | TauriRtcComponent | null;

type TaskFunction = (
    id?: string | null,
    deviceGroups?: UIDeviceGroup[],
    sessions?: sessionsType,
    rtc?: rtcType,
    update?: (macList: MacListProps[]) => Promise<void>,
    instance?: InstanceDTO,
) => Promise<Car[] | undefined>;

const tasks: Record<string, TaskFunction> = {
    // Tauri Browser fetch mac
    task1: async (id, deviceGroups, sessions, rtc) => {
        console.log(sessions);
        await simulateDelay(delayMs);
        // return [];
        return await handleDiscoverBrowser(id, deviceGroups, sessions, rtc);
    },
    // Tauri Headless fetch mac
    task2: async (id, deviceGroups, sessions, rtc, update, instance) => {
        console.log(sessions);
        await simulateDelay(delayMs);
        // return [];
        return await handleDiscoverHeadless(id, deviceGroups, sessions, rtc, update, instance);
    },
    // Tauri Browser fetch mac & update
    task3: async (id, deviceGroups, sessions, rtc, update) => {
        console.log(sessions);
        await simulateDelay(delayMs);
        // return [];
        return await handleDiscoverBrowser(id, deviceGroups, sessions, rtc, update);
    },
    // Tauri Headless fetch mac & update
    task4: async (id, deviceGroups, sessions, rtc, update, instance) => {
        console.log(sessions);
        await simulateDelay(delayMs);
        // return [];
        return await handleDiscoverHeadless(id, deviceGroups, sessions, rtc, update, instance);
    },
};

export default function RedirectPageQR() {
    const { data: deviceGroups } = useSWR<UIDeviceGroup[]>('devicegroups');
    const { data: instances } = useGetInstances();
    const sessions = useSessionsStore((state) => state.sessions);
    const rtc = useSessionsStore((state) => state.rtc);

    const navigate = useNavigate();
    const location = useLocation();
    const [taskStatus, setTaskStatus] = useState<TaskStatus>({
        error: false,
        loading: false,
        success: false,
        taskNotFound: false,
    });

    const queryParams = new URLSearchParams(location.search);
    const task = queryParams.get('task');
    const id = queryParams.get('id');

    const currentURL = window.location.href;

    const { trigger: submitDeviceGroups } = useSWRMutation(`devicegroups/${id}`, AddressMutate);

    const handleUpdateDevice = useCallback(
        async (macList: MacListProps[]) => {
            try {
                await submitDeviceGroups({
                    body: macList,
                    method: 'POST',
                    replaceRoute: `devicegroups/${id}/add_addresses`,
                });
            } catch (error) {
                alert(error);
            }
        },
        [id, submitDeviceGroups],
    );

    const [ip, setIp] = useState<string[]>([]);
    const [ipRes, setIpRes] = useState<string>('Empty');

    const handleDiscoverIPs = async () => {
        const addresses = await invoke<string[]>('get_if_addresses');
        setIp(addresses);
    };

    const MobileMacFetch = (instance: string | undefined) => {
        // eslint-disable-next-line no-async-promise-executor, @typescript-eslint/no-misused-promises
        return new Promise(async (resolve, reject) => {
            if (!instance) {
                reject('instance ID is missing.');
                return;
            }

            const testIp = ip[1];
            for (const ipScan of ip) {
                console.log('Starting scan for:', ipScan);
                try {
                    if (testIp === '0.0.0.0') {
                        throw new Error('Ip could not be fetched');
                    }
                    const res = await AddressFetchWithBody<{ mac: string }>(`instances/${instance}/macscan`, 'POST', {
                        ip: '192.168.31.16',
                    });
                    console.log(res);

                    const foundMacAddress = res.mac;
                    setIpRes(`Scanning SUCCESS: ${foundMacAddress}`);

                    if (!foundMacAddress) {
                        reject('Mac address not found.');
                        return;
                    }

                    resolve(foundMacAddress);
                } catch (error) {
                    setIpRes(`Scanning for failed for this IP: ${ipScan}`);
                    console.log(`Scanning for failed for this IP: ${ipScan}`);
                    console.error('Error fetching mac address data:', error);
                    reject('Error occurred while fetching mac address data.');
                }
            }
        });
    };

    useEffect(() => {
        const performTask = async () => {
            if (task && tasks[task] && sessions && rtc && instances) {
                setTaskStatus({ error: false, loading: true, success: false, taskNotFound: false });
                try {
                    await simulateDelay(delayMs);
                    // Start Task
                    const result = await tasks[task](id, deviceGroups, sessions, rtc, handleUpdateDevice, instances[0]);

                    if (result && result.length > 0) {
                        setTaskStatus({ error: false, loading: false, success: true, taskNotFound: false });
                        console.log('Finishing task segment...');
                        await simulateDelay(delayMs);

                        const resultQuery = result.map((i) => `address=${i.address}`).join('&');
                        setTimeout(() => {
                            id
                                ? navigate(`/device/${id}?task=${task}&${resultQuery}`, { state: { success: true } })
                                : navigate(`/device?task=${task}&${resultQuery}`, { state: { success: true } });
                        }, 3000);
                    } else {
                        if (task === 'task1') {
                            console.log('Trying task2...');
                            // Task 1 failed, Start Task 2
                            const result = tasks.task2 && (await tasks.task2(id, deviceGroups, sessions, rtc));

                            if (result && result.length > 0) {
                                setTaskStatus({ error: false, loading: false, success: true, taskNotFound: false });
                                console.log('Result found with task2...');
                                await simulateDelay(delayMs);

                                const carsQuery = result.map((car) => `address=${car.address}`).join('&');
                                setTimeout(() => {
                                    navigate(`/device?task=${task}&id=${id}&${carsQuery}`, {
                                        state: { success: true },
                                    });
                                }, 3000);
                            } else {
                                console.log('Task 2 Failed!');
                                setTaskStatus({ error: true, loading: false, success: false, taskNotFound: false });
                            }
                        } else if (task === 'task3') {
                            console.log('Trying task4...');
                            // Task 3 failed, Start Task 4
                            const result =
                                tasks.task4 &&
                                (await tasks.task4(id, deviceGroups, sessions, rtc, handleUpdateDevice, instances[0]));

                            if (result && result.length > 0) {
                                setTaskStatus({ error: false, loading: false, success: true, taskNotFound: false });
                                console.log('Updating car with task4...');
                                await simulateDelay(delayMs);

                                const resultQuery = result.map((car) => `address=${car.address}`).join('&');
                                setTimeout(() => {
                                    id
                                        ? navigate(`/device/${id}?task=${task}&${resultQuery}`, {
                                              state: { success: true },
                                          })
                                        : navigate(`/device?task=${task}&${resultQuery}`, { state: { success: true } });
                                }, 3000);
                            } else {
                                console.log('Task 4 Failed!');
                                setTaskStatus({ error: true, loading: false, success: false, taskNotFound: false });
                            }
                        } else {
                            console.log('Task Failed!');
                            setTaskStatus({ error: true, loading: false, success: false, taskNotFound: false });
                        }
                    }
                } catch {
                    console.log('Initial Task Failed!');
                    setTaskStatus({ error: true, loading: false, success: false, taskNotFound: false });
                }
            } else {
                console.log('No task found.');
                setTaskStatus({ error: false, loading: false, success: false, taskNotFound: true });
            }
        };

        void performTask();
    }, [task, id, navigate, sessions, rtc, deviceGroups, handleUpdateDevice, instances]);

    const renderContent = () => {
        switch (true) {
            case taskStatus.loading:
                return (
                    <>
                        <CircularProgress size={50} sx={{ color: '#1C4E71' }} />
                        <Stack
                            sx={{
                                textAlign: 'center',
                            }}
                        >
                            <Typography
                                sx={{
                                    color: '#1C4E71',
                                }}
                                variant="h4"
                            >
                                Renki is working
                            </Typography>
                            <Typography
                                sx={{
                                    color: '#1C4E71',
                                }}
                                variant="h6"
                            >
                                {id ? `Performing ${task} with ID: ${id}` : `Performing ${task}`}
                            </Typography>
                            <Typography
                                sx={{
                                    color: '#1C4E71',
                                }}
                                variant="h6"
                            >
                                Please wait...
                            </Typography>
                        </Stack>
                    </>
                );
            case taskStatus.taskNotFound:
                return (
                    <>
                        <Typography color="error" variant="h4">
                            Task not found
                        </Typography>
                        <Button color="primary" onClick={() => window.location.reload()} variant="contained">
                            Retry
                        </Button>
                    </>
                );
            case taskStatus.error:
                return (
                    <>
                        <Typography color="error" variant="h4">
                            Task failed
                        </Typography>
                        <Typography
                            sx={{
                                color: '#1C4E71',
                            }}
                            variant="h6"
                        >
                            An error occurred while performing the task
                        </Typography>
                        <Button color="primary" onClick={() => window.location.reload()} variant="contained">
                            Retry
                        </Button>
                    </>
                );
            case taskStatus.success:
                return (
                    <>
                        <CheckCircleIcon color="success" sx={{ fontSize: 50 }} />
                        <Stack
                            sx={{
                                textAlign: 'center',
                            }}
                        >
                            <Typography
                                sx={{
                                    color: '#1C4E71',
                                }}
                                variant="h4"
                            >
                                Task completed!
                            </Typography>
                            <Typography
                                sx={{
                                    color: '#1C4E71',
                                }}
                                variant="h6"
                            >
                                Redirecting...
                            </Typography>
                        </Stack>
                    </>
                );
            default:
                return null;
        }
    };

    const startTask = (task: string, id?: string) => {
        id ? navigate(`/qr?task=${task}&id=${id}`) : navigate(`/qr?task=${task}`);
    };

    const deviceId = 'e2e6d4dc-ba35-42da-a172-d23f6cdbd531';

    return (
        <Container disableGutters maxWidth={false} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Stack
                spacing={8}
                sx={{
                    py: 2,
                }}
            >
                <Stack
                    sx={{
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Typography>{currentURL}</Typography>
                    <Typography
                        sx={{
                            color: 'info.main',
                        }}
                    >
                        Loading: {taskStatus.loading.toString()}
                    </Typography>
                    <Typography
                        sx={{
                            color: 'success.main',
                        }}
                    >
                        Success: {taskStatus.success.toString()}
                    </Typography>
                    <Typography
                        sx={{
                            color: 'error.main',
                        }}
                    >
                        Error: {taskStatus.error.toString()}
                    </Typography>
                    <Typography>IP List: {JSON.stringify(ip)}</Typography>
                    <Typography>IP amount: {ip.length}</Typography>
                    <Typography variant="h6">IP status:{JSON.stringify(ipRes)}</Typography>
                </Stack>
                <Stack
                    spacing={{ md: 10, xs: 6 }}
                    sx={{
                        alignItems: 'center',
                    }}
                >
                    <Stack
                        spacing={2}
                        sx={{
                            alignItems: 'center',
                            textAlign: 'center',
                        }}
                    >
                        {renderContent()}
                    </Stack>
                    <Stack direction={{ md: 'row', xs: 'column' }} spacing={4}>
                        <Button onClick={() => startTask('task1')} variant="outlined">
                            Create Browser
                        </Button>
                        <Button onClick={() => startTask('task2')} variant="outlined">
                            Create Headless
                        </Button>
                        <Button onClick={() => startTask('task3', deviceId)} variant="outlined">
                            Update Browser
                        </Button>
                        <Button onClick={() => startTask('task4', deviceId)} variant="outlined">
                            Update Headless
                        </Button>
                        <Button onClick={() => navigate('/')} variant="contained">
                            Home
                        </Button>
                        <Button onClick={handleDiscoverIPs} variant="contained">
                            IPs
                        </Button>
                        <Button onClick={() => instances && MobileMacFetch(instances[0]?.id)} variant="contained">
                            Get by IP
                        </Button>
                    </Stack>
                </Stack>
            </Stack>
        </Container>
    );
}
